
import { useState } from 'react';

interface QRCodeGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QRCodeGenerator({ isOpen, onClose }: QRCodeGeneratorProps) {
  const [qrText, setQrText] = useState('https://maveskytravels.com');
  const [qrSize, setQrSize] = useState(200);
  const [showQR, setShowQR] = useState(false);

  const generateQRCode = () => {
    if (qrText.trim()) {
      setShowQR(true);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
    if (canvas) {
      const link = document.createElement('a');
      link.download = 'mavesky-qr-code.png';
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const resetQR = () => {
    setShowQR(false);
    setQrText('https://maveskytravels.com');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">QR Code Generator</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="space-y-6">
            {!showQR ? (
              <>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Text or URL to encode:
                  </label>
                  <textarea
                    value={qrText}
                    onChange={(e) => setQrText(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:outline-none focus:border-blue-500 resize-none"
                    placeholder="Enter text, URL, or contact info..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Examples: Website URL, contact info, WiFi credentials, etc.
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    QR Code Size: {qrSize}px
                  </label>
                  <input
                    type="range"
                    min="150"
                    max="400"
                    value={qrSize}
                    onChange={(e) => setQrSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={resetQR}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Reset
                  </button>
                  <button
                    onClick={generateQRCode}
                    disabled={!qrText.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Generate QR
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="bg-gray-50 rounded-xl p-6 mb-4">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(qrText)}&format=png&ecc=M&margin=20&bgcolor=FFFFFF&color=000000`}
                      alt="Generated QR Code"
                      className="mx-auto rounded-lg shadow-lg"
                      style={{ width: qrSize, height: qrSize }}
                    />
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600 font-medium mb-1">Encoded content:</p>
                    <p className="text-sm text-gray-800 break-words">{qrText}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={resetQR}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Generate New
                  </button>
                  <button
                    onClick={downloadQR}
                    className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-download-line"></i>
                    </div>
                    Download
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                <i className="ri-information-line text-yellow-600"></i>
              </div>
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">QR Code Uses:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Share your website or booking page</li>
                  <li>• Contact information for easy saving</li>
                  <li>• WiFi credentials for customers</li>
                  <li>• Promotional links and offers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
